import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
} from "@mui/material";
import { confirmUser, resendConfirmationCode } from "../services/auth";
import { LogoImage } from "../components/layout/Logo";

const ConfirmAccount: React.FC = () => {
  const [confirmationCode, setConfirmationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Get email from URL parameters (passed from register page)
  const urlParams = new URLSearchParams(location.search);
  const email = urlParams.get("email") || location.state?.email || "";

  useEffect(() => {
    if (!email) {
      navigate("/login");
    }
  }, [email, navigate]);

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!confirmationCode.trim()) {
      setError("Please enter the confirmation code");
      return;
    }

    try {
      setError(null);
      setLoading(true);

      await confirmUser(email, confirmationCode.trim());

      setSuccess("Account confirmed successfully! Redirecting to login...");

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate("/login", {
          state: {
            message: "Account confirmed! You can now sign in.",
            email: email,
          },
        });
      }, 2000);
    } catch (err: any) {
      setError(err.message || "Confirmation failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setError(null);
      setResendLoading(true);

      await resendConfirmationCode(email);
      setSuccess("Confirmation code resent! Check your email.");
    } catch (err: any) {
      setError(err.message || "Failed to resend code. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  if (!email) {
    return null; // Will redirect to login
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 3,
            }}
          >
            <LogoImage width={120} height={120} noText={false} />
            <Typography component="h2" variant="h5" gutterBottom>
              Confirm Your Account
            </Typography>
            <Typography variant="body2" color="textSecondary">
              We've sent a confirmation code to <strong>{email}</strong>
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleConfirm} sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              id="confirmationCode"
              label="Confirmation Code"
              name="confirmationCode"
              value={confirmationCode}
              onChange={(e) => setConfirmationCode(e.target.value)}
              placeholder="6-digit code"
              inputProps={{
                maxLength: 6,
                style: {
                  textAlign: "center",
                  fontSize: "1.5rem",
                  letterSpacing: "0.5rem",
                },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={loading || !confirmationCode.trim()}
            >
              {loading ? "Confirming..." : "Confirm Account"}
            </Button>

            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Didn't receive the code?
              </Typography>

              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={handleResendCode}
                disabled={resendLoading}
                sx={{ cursor: resendLoading ? "default" : "pointer" }}
              >
                {resendLoading ? "Sending..." : "Resend confirmation code"}
              </Link>
            </Box>

            <Box sx={{ textAlign: "center", mt: 3 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => navigate("/login")}
              >
                Back to Sign In
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ConfirmAccount;
